import { Pills, RadioGroup, type PillsStatus, type PillsVariant } from '@amelio/ui-library';

export interface StatusTilePill {
    variant: PillsVariant;
    status?: PillsStatus;
    className?: string;
}

export interface StatusTileOption<T extends string> {
    value: T;
    label: string;
    /** The pill the status wears everywhere else; the tile takes its colour from it. */
    pill: StatusTilePill;
}

interface StatusRadioTilesProps<T extends string> {
    name: string;
    /** The id of the visible label that names the group. */
    labelledBy: string;
    options: StatusTileOption<T>[];
    value: T;
    onChange: (value: T) => void;
    dataTestId?: string;
}

// The whole option is the radio's label, so its padding selects too; the focus ring shows on the
// label while the visually hidden radio inside it has keyboard focus. The border stays in the box
// at rest — transparent, not absent — so choosing an option never shifts the row by two pixels.
const TILE_BASE =
    'flex h-12 w-full cursor-pointer items-center justify-center rounded-lg border-2 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-800 has-[:focus-visible]:ring-offset-2';

interface TileChrome {
    checked: string;
    idle: string;
}

// Each option answers in its own status colour — the same pair the pill inside it already wears —
// so the chosen status reads from the tile, not only from the label.
const TONED_CHROME: Partial<Record<PillsStatus, TileChrome>> = {
    active: {
        checked: 'border-success-50 bg-success-25',
        idle: 'border-transparent bg-white hover:bg-success-25',
    },
    draft: {
        checked: 'border-warning-50 bg-warning-25',
        idle: 'border-transparent bg-white hover:bg-warning-25',
    },
    inactive: {
        checked: 'border-error-50 bg-error-25',
        idle: 'border-transparent bg-white hover:bg-error-25',
    },
};

const NEUTRAL_CHROME: TileChrome = {
    checked: 'border-base-100 bg-base-25',
    idle: 'border-transparent bg-white hover:bg-base-25',
};

const chromeFor = (pill: StatusTilePill): TileChrome =>
    (pill.status && TONED_CHROME[pill.status]) || NEUTRAL_CHROME;

const MAX_COLUMNS = 4;

/** A status picked from coloured tiles, one per status, each showing the pill it is known by. */
export default function StatusRadioTiles<T extends string>({
    name,
    labelledBy,
    options,
    value,
    onChange,
    dataTestId,
}: StatusRadioTilesProps<T>) {
    const pillsByValue = new Map(options.map((option) => [option.value, option.pill]));
    const columns = Math.min(Math.max(options.length, 1), MAX_COLUMNS) as 1 | 2 | 3 | 4;

    return (
        // The library's fieldset has no name of its own: the group takes the visible label
        // through aria-labelledby.
        <div
            role={'radiogroup'}
            aria-labelledby={labelledBy}
        >
            <RadioGroup
                name={name}
                options={options.map(({ value: optionValue, label }) => ({
                    value: optionValue,
                    label,
                }))}
                value={value}
                onChange={(picked) => onChange(picked as T)}
                columns={columns}
                dataTestId={dataTestId}
                renderOption={({
                    option,
                    isChecked,
                    onSelect,
                    name: groupName,
                    dataTestId: id,
                }) => {
                    const pill = pillsByValue.get(option.value as T) ?? { variant: 'default' };
                    const chrome = chromeFor(pill);

                    return (
                        <label
                            className={`${TILE_BASE} ${isChecked ? chrome.checked : chrome.idle}`}
                            data-testid={id}
                        >
                            <input
                                type={'radio'}
                                className={'sr-only'}
                                name={groupName}
                                value={option.value}
                                checked={isChecked}
                                onChange={onSelect}
                            />

                            <Pills
                                variant={pill.variant}
                                status={pill.status}
                                size={'sm'}
                                className={pill.className}
                            >
                                {option.label}
                            </Pills>
                        </label>
                    );
                }}
            />
        </div>
    );
}
